import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { wave, waveQueries } from '@round/api';
import { datadogRum } from '@datadog/browser-rum';
import { ChevronDown, CircleAlert, Clock3, LogIn } from 'lucide-react';
import { showNotification } from '@/utility/showNotification';
import { formatHandle } from '@/utility/formatHandle';
import AudioLinks from '@/creator/components/AudioLinks';
import CampaignArtwork from '@/creator/components/CampaignArtwork';
import BudgetUsedMeter from '@/creator/components/BudgetUsedMeter';
import WaveLogo from '@/creator/components/WaveLogo';
import { BriefText } from '@/ui/BriefSection/BriefSection';
import { formatDeadlineCountdown } from '@/ui/DeadlinePill/DeadlinePill';
import PublicLoginTakeover from '@/ui/PublicLoginTakeover/PublicLoginTakeover';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import CpmRateNote from '@/creator/components/CpmRateNote';
import { getInviteOffer, PLATFORM_LABEL } from '@/utility/campaignDisplay';
import { getRespondToInviteBody } from '@/utility/inviteContract';
import ContractList from '@/creator/components/ContractCard/ContractList';
import { getOfferPosts } from '@/utility/offerPosts';
import type { ContractResponse } from '@/creator/components/ContractCard/ContractCard';
import { countOffers, getOfferSummary } from '@/utility/contracts';
import '@/creator-app.css';

const SUPPORT_EMAIL = 'hello@wave.me';

const Shell = ({ children, headerRight }: { children: ReactNode; headerRight?: ReactNode }) => (
    <div className="public-campaign-page public-campaign-page--public">
        <header className="public-campaign-header">
            <WaveLogo compact />
            {headerRight}
        </header>
        <main className="public-campaign-main">{children}</main>
    </div>
);

const StateNotice = ({ icon, title, body }: { icon: ReactNode; title: string; body: ReactNode }) => (
    <section className="submission-limit-notice">
        {icon}
        <div>
            <h2>{title}</h2>
            <p>{body}</p>
        </div>
    </section>
);

const MINUTE_MS = 60 * 1000;

const InvitePage = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAuthenticated } = useWaveAuth();
    const query = useQuery(waveQueries.invite.options.detail(uuid, isAuthenticated));
    const contractsQuery = useQuery(waveQueries.contract.options.forInvite(uuid, isAuthenticated));
    const [showLogin, setShowLogin] = useState(false);
    // The brief governs whether a post gets approved, so this page leads with it.
    const [briefExpanded, setBriefExpanded] = useState(true);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const revealLogin = () => {
        setShowLogin(true);
    };

    const handleAuthenticated = async (): Promise<void> => {
        if (!uuid) {
            return;
        }
        // Login changes the ownership metadata returned for this UUID. Fetch the
        // authenticated variant before deciding which stable route owns the viewer.
        const invite = await queryClient.fetchQuery(waveQueries.invite.options.detail(uuid, true));
        if (invite.viewer_is_owner) {
            navigate(`/campaigns/${uuid}`, { replace: true });
            return;
        }
        navigate(`/c/${invite.public_token}`, { replace: true });
    };

    // The home wallet lists this invite too; drop its cache so acting here shows there.
    const refetchInvite = () =>
        Promise.all([
            query.refetch(),
            contractsQuery.refetch(),
            queryClient.invalidateQueries({ queryKey: waveQueries.invite.keys.mine() }),
            queryClient.invalidateQueries({ queryKey: waveQueries.contract.keys.all() }),
        ]);

    useEffect(() => {
        if (!query.isError) {
            return;
        }
        // Mirrors microwave's useMicrosPaymentRequest: report invite-load failures to Datadog RUM.
        datadogRum.addError(query.error ?? new Error('Failed to load Wave invite'), { uuid });
    }, [query.isError, query.error, uuid]);

    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), MINUTE_MS);
        return () => window.clearInterval(intervalId);
    }, []);

    const handleRespond = async (contract: wave.CreatorContract, answer: ContractResponse): Promise<boolean> => {
        if (!uuid) {
            return false;
        }
        try {
            const result = await wave.respondToInvite(
                uuid,
                getRespondToInviteBody(contract, answer.response, answer.reason ?? '', answer.creatorAccounts)
            );
            if (result.status === 200) {
                await refetchInvite();
                setNowMs(Date.now());
                // The backend answers an offer it can no longer change with its unchanged state.
                if (result.data.response !== answer.response) {
                    showNotification('This offer couldn’t be changed. Refresh the page and try again.', 'error');
                    return false;
                }
                showNotification(
                    answer.response === 'accepted'
                        ? "You're in! We've let the brand know."
                        : "Thanks for letting us know you're not taking this one.",
                    'success'
                );
                return true;
            }
            if (result.status === 409) {
                showNotification(result.data.reason, 'error');
                await refetchInvite();
                return false;
            }
            showNotification('We couldn’t save your response. Please try again.', 'error');
            return false;
        } catch {
            showNotification('We couldn’t save your response. Please try again.', 'error');
            return false;
        }
    };

    const handleSubmitPost = async (contract: wave.CreatorContract, postUrl: string): Promise<string | null> => {
        if (!uuid) {
            return null;
        }
        try {
            const result = await wave.submitPost(uuid, { post_url: postUrl, contract_id: contract.id });
            if (result.status === 201 || result.status === 200) {
                await refetchInvite();
                showNotification(
                    result.status === 201
                        ? 'Post submitted. It’s in for review.'
                        : 'That post is already in for this offer.',
                    result.status === 201 ? 'success' : 'info'
                );
                return null;
            }
            if (result.status === 409) {
                await refetchInvite();
                return result.data.reason || 'This offer can’t take another post right now.';
            }
            datadogRum.addError(new Error('Wave contract submission client error'), { response: result, postUrl });
            if (result.status === 400) {
                const error = result.data.post_url ?? result.data.contract_id;
                return (
                    (Array.isArray(error) ? error[0] : error) ?? 'We couldn’t accept that link. Check it and try again.'
                );
            }
            return 'We couldn’t submit your post. Please try again.';
        } catch (e) {
            datadogRum.addError(e, { postUrl });
            return `Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`;
        }
    };

    if (showLogin) {
        return (
            <PublicLoginTakeover
                backLabel="Back to invite"
                onBack={() => setShowLogin(false)}
                onAuthenticated={handleAuthenticated}
            />
        );
    }

    if (isAuthenticated && query.data) {
        return query.data.viewer_is_owner ? (
            <Navigate to={`/campaigns/${query.data.id}`} replace />
        ) : (
            <Navigate to={`/c/${query.data.public_token}`} replace />
        );
    }

    if (query.isLoading || contractsQuery.isLoading) {
        return (
            <Shell>
                <p className="page">Loading invite…</p>
            </Shell>
        );
    }

    if (query.isError || !query.data) {
        return (
            <Shell>
                <StateNotice
                    icon={<CircleAlert size={16} />}
                    title="This link is invalid or expired"
                    body={
                        <>
                            We couldn&apos;t find this invite. Double-check the link, or reach out to {SUPPORT_EMAIL} if
                            you think this is a mistake.
                        </>
                    }
                />
            </Shell>
        );
    }

    const invite = query.data;
    const contracts = contractsQuery.data ?? [];
    const offerCount = countOffers(contracts);
    const showOfferCount = offerCount > 1;
    const offerSummary = getOfferSummary(contracts);
    const submissionAvailability = invite.submission_availability;
    // A lapsed hold is this invite's own, and re-reservable: not a closed campaign.
    const isTerminal =
        (submissionAvailability !== 'open' && submissionAvailability !== 'reservation_expired') ||
        invite.status === 'missed';
    const offer = getInviteOffer(invite);
    // Creators post as the account, not as themselves, so the identity comes from the
    // invited accounts: several of them count rather than naming the person.
    const invitedPlatforms = [...new Set(invite.allowed_accounts.map((account) => account.platform))];
    const postingAs =
        invite.allowed_accounts.length === 1
            ? formatHandle(invite.allowed_accounts[0].platform, invite.allowed_accounts[0].handle)
            : `${invite.allowed_accounts.length} accounts`;
    const postingAsPlatform =
        invitedPlatforms.length > 0
            ? invitedPlatforms.map((platform) => PLATFORM_LABEL[platform]).join(' and ')
            : PLATFORM_LABEL[invite.platform];
    const timelineLabel = !isTerminal
        ? invite.deadline
            ? formatDeadlineCountdown(invite.deadline, nowMs)
            : 'Open for submissions'
        : 'Submissions closed';

    const headerRight = (
        <button className="public-login-button" type="button" onClick={revealLogin}>
            <LogIn size={14} /> <span>Sign in</span>
        </button>
    );

    return (
        <Shell headerRight={headerRight}>
            <section className="public-campaign-hero">
                <CampaignArtwork src={invite.artwork_url} alt={`${invite.release_name} artwork`} size="detail" />
                <div className="public-campaign-hero__copy">
                    <h1>{invite.release_name}</h1>
                    <p>{`A creator campaign from ${invite.artist_name}.`}</p>
                    <span className="deadline">
                        <Clock3 size={14} /> <span>{timelineLabel}</span>
                    </span>
                    <BudgetUsedMeter percentage={invite.budget_used_percentage} />
                </div>
            </section>

            <section className="public-invite-summary" aria-label="Your offer">
                {showOfferCount ? (
                    <div>
                        <span>Your offers</span>
                        <strong>{offerCount === 1 ? '1 offer' : `${offerCount} offers`}</strong>
                        <small>Each has its own terms below.</small>
                    </div>
                ) : offerSummary ? (
                    <div>
                        <span>{offerSummary.label}</span>
                        <strong>{offerSummary.amount}</strong>
                        <small>{offerSummary.detail}</small>
                        {invite.campaign_type === 'cpm' ? <CpmRateNote /> : null}
                    </div>
                ) : (
                    <div>
                        <span>{invite.campaign_type === 'cpm' ? 'Maximum earnable' : 'Per-post offer'}</span>
                        <strong>{offer.amount}</strong>
                        <small>{offer.detail}</small>
                        {invite.campaign_type === 'cpm' ? <CpmRateNote /> : null}
                    </div>
                )}
                <div>
                    <span>Posting as</span>
                    <strong className="public-invite-summary__account">{postingAs}</strong>
                    <small>{postingAsPlatform}</small>
                </div>
            </section>

            <div className="public-campaign-layout">
                <div className="public-campaign-content">
                    {invite.audios.length > 0 ? (
                        <section className="public-section">
                            <div className="public-section__heading">
                                <h2>{invite.audios.length === 1 ? 'The audio' : 'The audios'}</h2>
                            </div>
                            <AudioLinks audios={invite.audios} />
                        </section>
                    ) : null}

                    {invite.brief.trim() ? (
                        <section className={`public-section public-brief ${briefExpanded ? 'is-expanded' : ''}`}>
                            <div className="public-section__heading">
                                <h2>The brief</h2>
                                <button
                                    type="button"
                                    onClick={() => setBriefExpanded((current) => !current)}
                                    aria-expanded={briefExpanded}
                                >
                                    <span>{briefExpanded ? 'Collapse brief' : 'Expand brief'}</span>
                                    <ChevronDown size={14} />
                                </button>
                            </div>
                            <div className="public-brief__copy">
                                <BriefText brief={invite.brief} />
                            </div>
                        </section>
                    ) : null}

                    {contractsQuery.isError ? (
                        <StateNotice
                            icon={<CircleAlert size={16} />}
                            title="We couldn't load your offers"
                            body="Refresh the page to try again."
                        />
                    ) : (
                        <ContractList
                            contracts={contracts}
                            invite={invite}
                            posts={getOfferPosts({ contracts, invite })}
                            nowMs={nowMs}
                            onRespond={submissionAvailability === 'ended' ? undefined : handleRespond}
                            onSubmitPost={submissionAvailability === 'ended' ? undefined : handleSubmitPost}
                        />
                    )}

                    {submissionAvailability === 'ended' && !invite.has_submitted ? (
                        <StateNotice
                            icon={<Clock3 size={16} />}
                            title="Submissions are closed"
                            body="The window for this campaign has ended. If you already posted, email your Wave contact so we can sort it out."
                        />
                    ) : null}
                </div>

                <aside className="public-action-card">
                    <button className="public-action-card__login" type="button" onClick={revealLogin}>
                        Have a Wave account? Sign in
                    </button>
                </aside>
            </div>
        </Shell>
    );
};

export default InvitePage;
